import { CancellableAPIRequest, makeCancellableAPIRequest } from "restAPI";
import {
  BaseResponse,
  LogUploadCompleteRequest,
  LogUploadCompleteResponse,
  LogUploadPresignRequest,
  LogUploadPresignResponse,
} from "types/api/aiRiskMgmt";

const BASE_URL = "/api/v1/integrations";

// Types
export type IntegrationConfigStatus = "ACTIVE" | "INACTIVE" | "DELETED";
export type ValidationStatus = "PENDING" | "PASSED" | "FAILED";
export type OperationalStatus = "ACTIVE" | "INACTIVE" | "ERROR" | "SYNCING";
export type HealthStatus = "HEALTHY" | "DEGRADED" | "UNHEALTHY" | "UNKNOWN";
export type SyncStatus = "SUCCESS" | "PARTIAL" | "FAILED";

export interface BaseIntegrationConfig {
  id: number;
  uuid: string;
  tenantId: number;
  businessUnitId?: number;
  integrationType: string;
  name: string;
  description?: string;
  status: IntegrationConfigStatus;
  validationStatus: ValidationStatus;
  validationMessage?: string;
  operationalStatus: OperationalStatus;
  healthStatus: HealthStatus;
  lastSyncAt?: string;
  lastSyncStatus?: SyncStatus;
  lastSyncMessage?: string;
  itemsProcessed24h?: number;
  itemsProcessedTotal?: number;
  lastErrorAt?: string;
  lastErrorMessage?: string;
  consecutiveErrors?: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: number;
}

export interface BaseIntegrationConfigRequest {
  name: string;
  description?: string;
  integrationType?: string;
}

export interface IntegrationConfigListResponse<
  T extends BaseIntegrationConfig,
> {
  configurations: T[];
  total: number;
  totalElements?: number;
}

export interface IntegrationValidationResult {
  success: boolean;
  message: string;
  details?: Record<string, boolean>;
}

export interface IntegrationSyncResult {
  success: boolean;
  message: string;
}

export interface CloudFormationSetupInfo {
  templateUrl: string;
  prefilledParameters: Record<string, string>;
  customerParameters: string[];
  quickCreateUrl: string;
  integrationType: string;
}

// API
const GenericIntegrationAPI = {
  getConfigurations<T extends BaseIntegrationConfig>(
    integrationType: string,
    params?: Record<string, unknown>,
  ): CancellableAPIRequest<IntegrationConfigListResponse<T>> {
    return makeCancellableAPIRequest("GET", BASE_URL, {
      params: { integrationType, ...params },
    });
  },

  createConfiguration<
    TRequest extends BaseIntegrationConfigRequest,
    TResponse extends BaseIntegrationConfig,
  >(integrationType: string, data: TRequest): CancellableAPIRequest<TResponse> {
    return makeCancellableAPIRequest("POST", BASE_URL, {
      data: { ...data, integrationType },
    });
  },

  updateConfiguration<
    TRequest extends Partial<BaseIntegrationConfigRequest>,
    TResponse extends BaseIntegrationConfig,
  >(id: number, data: TRequest): CancellableAPIRequest<TResponse> {
    return makeCancellableAPIRequest("PUT", `${BASE_URL}/${id}`, {
      data: { ...data, id },
    });
  },

  deleteConfiguration(id: number): CancellableAPIRequest<void> {
    return makeCancellableAPIRequest("DELETE", `${BASE_URL}/${id}`);
  },

  validateConfiguration(
    id: number,
  ): CancellableAPIRequest<IntegrationValidationResult> {
    return makeCancellableAPIRequest("POST", `${BASE_URL}/${id}/validate`);
  },

  triggerSync(id: number): CancellableAPIRequest<IntegrationSyncResult> {
    return makeCancellableAPIRequest("POST", `${BASE_URL}/${id}/sync`);
  },

  triggerScan(
    id: number,
    integrationType: string,
  ): CancellableAPIRequest<IntegrationSyncResult> {
    return makeCancellableAPIRequest("POST", `${BASE_URL}/${id}/trigger-scan`, {
      params: { integrationType },
    });
  },

  getCloudFormationTemplate(
    integrationType: string,
  ): CancellableAPIRequest<CloudFormationSetupInfo> {
    return makeCancellableAPIRequest(
      "GET",
      `${BASE_URL}/types/${integrationType}/cloudformation`,
    );
  },

  getLogUploadPresignedUrl(
    data: LogUploadPresignRequest,
  ): CancellableAPIRequest<BaseResponse<LogUploadPresignResponse>> {
    return makeCancellableAPIRequest("POST", `${BASE_URL}/log-upload/presign`, {
      data,
    });
  },

  notifyLogUploadComplete(
    data: LogUploadCompleteRequest,
  ): CancellableAPIRequest<BaseResponse<LogUploadCompleteResponse>> {
    return makeCancellableAPIRequest(
      "POST",
      `${BASE_URL}/log-upload/complete`,
      {
        data,
      },
    );
  },
};

export default GenericIntegrationAPI;

// Helper to create typed API for specific integration
export function createIntegrationAPI<
  TConfig extends BaseIntegrationConfig,
  TRequest extends BaseIntegrationConfigRequest,
>(integrationType: string) {
  return {
    getConfigurations(
      params?: Record<string, unknown>,
    ): CancellableAPIRequest<IntegrationConfigListResponse<TConfig>> {
      return GenericIntegrationAPI.getConfigurations<TConfig>(
        integrationType,
        params,
      );
    },
    createConfiguration(data: TRequest): CancellableAPIRequest<TConfig> {
      return GenericIntegrationAPI.createConfiguration<TRequest, TConfig>(
        integrationType,
        data,
      );
    },
    updateConfiguration(
      id: number,
      data: Partial<TRequest>,
    ): CancellableAPIRequest<TConfig> {
      return GenericIntegrationAPI.updateConfiguration<
        Partial<TRequest>,
        TConfig
      >(id, data);
    },
    deleteConfiguration(id: number): CancellableAPIRequest<void> {
      return GenericIntegrationAPI.deleteConfiguration(id);
    },
    validateConfiguration(
      id: number,
    ): CancellableAPIRequest<IntegrationValidationResult> {
      return GenericIntegrationAPI.validateConfiguration(id);
    },
    triggerSync(id: number): CancellableAPIRequest<IntegrationSyncResult> {
      return GenericIntegrationAPI.triggerSync(id);
    },
    getCloudFormationTemplate(): CancellableAPIRequest<CloudFormationSetupInfo> {
      return GenericIntegrationAPI.getCloudFormationTemplate(integrationType);
    },
  };
}
